import Api from "./api";
import { AddressInfo } from "../models/address-info";
import { HousingInfo } from "../models/housing-info";

export class HomeInsuranceApi {
    static createRequest({
        documentId,
        documentType,
        email,
        mobile,
        recaptchaToken,
        channel,
    }) {
        const data = { documentId, documentType, email, mobile };
        if (channel) {
            data.channel = channel;
        }
        return Api.post("insurances/home/requests", {
            data,
            customHeader: {
                "X-Captcha-Token": recaptchaToken,
            },
        });
    }

    static createEstimated({
        requestId,
        currency,
        estimatedBelongingsAmount,
        estimatedPropertyAmount,
        declaredBelongingsAmount,
        declaredPropertyAmount,
    }) {
        return Api.post(`insurances/home/requests/${requestId}/quote/estimated`, {
            data: {
                currency,
                estimatedBelongingsAmount,
                estimatedPropertyAmount,
                declaredPropertyAmount,
                declaredBelongingsAmount,
            },
            isPublicRequest: false,
        });
    }

    static updatePersonalData({ requestId, clientType, insuranceType, planType }) {
        return Api.patch(`insurances/home/requests/${requestId}`, {
            data: { clientType, insuranceType, planType },
            isPublicRequest: false,
        });
    }

    static getUbigeos() {
        return Api.get("geographic-locations", {
            isPublicRequest: true,
        });
    }

    static saveSelectedCoverages({ requestId, selectedCoveragesCodes = [] }) {
        return Api.patch(
            `insurances/home/requests/${requestId}/quote/estimated/selected`,
            {
                data: selectedCoveragesCodes,
                isPublicRequest: false,
            }
        );
    }

    static calculateHomePrice({ locationId, propertyType, squareMeter }) {
        return Api.get(
            `geographic-locations/${locationId}/calculation/${propertyType}/${squareMeter}`
        );
    }

    static saveHousing({ requestId, housingInfo = new HousingInfo() }) {
        const { addressInfo, location } = housingInfo;
        return Api.post(`insurances/home/requests/${requestId}/property/register`, {
            data: {
                acceptTerms: true,
                ubigeo: location.value,
                propertyType: housingInfo.housingType,
                materialType: housingInfo.buildingMaterial,
                constructionYear: new Date().getFullYear() - housingInfo.housingAge,
                squareMeter: housingInfo.squareMeters,
                address: addressInfo.streetName,
                basement: Number(housingInfo.basements || 0),
                floors: Number(housingInfo.floorsNumber || 0),
                containsDeposit: housingInfo.hasStores,
                interiorNumber: String(housingInfo.interiorNumber),
                systemSecurityActive: housingInfo.securitySystem,
                block: addressInfo.block,
                lotNumber: String(addressInfo.lotNumber),
                number: String(addressInfo.streetNumber),
                streetType: addressInfo.streetType,
                urbanizationName: addressInfo.urbanizationName,
                urbanizationType: addressInfo.urbanizationType,
                posX: addressInfo.longitude || 0,
                posY: addressInfo.latitude || 0,
            },
            isPublicRequest: false,
        });
    }

    static saveAddressInfo({ requestId, addressInfoRequest = new AddressInfo() }) {
        return Api.post(`insurances/home/requests/${requestId}/property/update`, {
            data: {
                address: addressInfoRequest.streetName,
                basement: Number(addressInfoRequest.basements || 0),
                block: addressInfoRequest.block,
                containsDeposit: addressInfoRequest.hasStores,
                floors: Number(addressInfoRequest.floorsNumber || 0),
                interiorNumber: String(addressInfoRequest.departmentNumber),
                lotNumber: String(addressInfoRequest.lotNumber),
                number: String(addressInfoRequest.streetNumber),
                streetType: addressInfoRequest.streetType,
                urbanizationName: addressInfoRequest.urbanizationName,
                urbanizationType: addressInfoRequest.urbanizationType,
                systemSecurityActive: addressInfoRequest.securitySystem,
            },
            isPublicRequest: false,
        });
    }

    static processPayment({ requestId, paymentRequest }) {
        return Api.post(`insurances/home/requests/${requestId}/payment`, {
            data: paymentRequest,
            isPublicRequest: false,
        });
    }

    static sendDataCRM({ requestId, metadata }) {
        return Api.patch(`insurances/home/requests/${requestId}/metadata`, {
            data: { metadata },
            isPublicRequest: false,
        });
    }

    static saveStartEnsureDate({ requestId, startEnstureDate }) {
        return Api.patch(`insurances/home/requests/${requestId}/start`, {
            data: {
                startDate: startEnstureDate,
            },
            isPublicRequest: false,
        });
    }

    static startQuote({ requestId }) {
        return Api.post(`insurances/home/requests/${requestId}/quote`, {
            isPublicRequest: false,
        });
    }

    static verifyQuoteStatus({ requestId }) {
        return Api.get(`insurances/home/requests/${requestId}/quote`, {
            isPublicRequest: false,
        });
    }

    static getActivePromotion({ channel }) {
        const _params = channel ? { channelId: channel } : null;
        return Api.get(`insurances/home/promotions/active`, {
            isPublicRequest: false,
            params: _params,
        });
    }

    static validateIfDocumentNumberExists(documentNumber, documentType = "DNI") {
        return Api.patch("insurances/home/customer", {
            isPublicRequest: true,
            data: {
                documentId: documentNumber,
                documentType,
            },
        });
    }

    static updateCustomerInfo({
        requestId,
        birthDate,
        firstSurname,
        lastSurname,
        names,
    }) {
        return Api.patch(`insurances/home/requests/${requestId}/customer`, {
            data: {
                birthDate,
                firstSurname,
                lastSurname,
                names,
            },
            isPublicRequest: false,
        });
    }

    static savePersonalDatConsent({ requestId }) {
        return Api.patch(
            `insurances/home/requests/${requestId}/customer/personal-data-consent`,
            {
                params: {
                    "personal-data-consent": true,
                },
                isPublicRequest: false,
            }
        );
    }

    static updateUserPotencialId({ requestId, potencialId }) {
        return Api.patch(`insurances/home/requests/${requestId}/potencialId`, {
            data: {
                potencialId,
            },
            isPublicRequest: false,
        });
    }

    static verifyPayment({ requestId }) {
        return Api.get(`insurances/home/requests/${requestId}/payment`, {
            isPublicRequest: false,
        });
    }

    static saveContactConsent({ requestId, contactConsent }) {
        return Api.patch(
            `insurances/home/requests/${requestId}/customer/assisted-sale-consent`,
            {
                params: {
                    "assisted-sale-consent": contactConsent,
                },
                isPublicRequest: false,
            }
        );
    }
}
